<template>
  <div v-if="isLoading" class="isLoadingDiv">
    <ProgressSpinner />
  </div>
  <div v-else>
    <div class="p-col-12 paddedCol p-mt-5 p-text-left">
      <h1 class="title is-1 p-mt-3" style="display: inline">{{ $t("public-webpage") }}</h1>
    </div>

    <div class="p-col-12 whiteBg rounded p-text-left">
      <div class="p-col-12">
        <h2 class="title is-5 mb-5">{{ $t("webpage-url") }}</h2>

        <div class="p-d-flex p-align-center">
          <h3>
            {{ $t("public-webpage-url") }}
            <a :href="buildUrl" target="_blank">
              {{ buildUrl }}
            </a>
          </h3>
          <span v-if="!slugClaimed" class="ml-4 cursorpointer text-muted fs-small-14" @click="updateOrganizationSlug">
            <font-awesome-icon :icon="['fal', 'edit']" />
            {{ $t("edit-url") }}
          </span>
        </div>
      </div>
    </div> 
  
    <div class="p-col-12 whiteBg rounded p-text-left mt-5">
      <div class="p-col-12">
        <h2 class="title is-5 mb-5">{{ $t('league-url') }}</h2>
        <div class="p-d-flex p-align-center">
          <h3>
            {{ $t('league-url') }} :
            <a :href="buildLeagueUrl" target="_blank">
              {{ buildLeagueUrl }}
            </a>
          </h3>
          <span class="ml-4 cursorpointer text-muted fs-small-14" @click="updateLeagueSlug">
            <font-awesome-icon :icon="['fal', 'edit']" />
            {{ $t("edit-url") }}
          </span>
        </div>
        <br/>
        <div class="p-d-flex p-align-center">
          <h5>
            {{ $t("selected-season") }}
            <b class="fw-500">{{ (selectedSeason && selectedSeason.name) || $t("please-select-a-season") }}</b>
          </h5>
          <span class="ml-4 cursorpointer text-muted fs-small-14" @click="() => (editSeasonDialog = true)">
            <font-awesome-icon :icon="['fal', 'edit']" />
            {{ $t("edit-season") }}
          </span>
        </div>
        
        <br/>
        
        <div class="p-d-flex p-align-center">
          <div class="p-field-checkbox">
            <Checkbox v-model="showLeaguePage" :binary="true" @change="() => (showSaveButton = true)" />
            <label>{{ $t('display-league-on-webpage') }}</label>
          </div>
        </div>

        <div v-if="showSaveButton" class="mt-4">
          <Button @click="publishLeague">{{ $t("Button.save") }}</Button>
        </div>
      </div>
    </div>
    
    <div class="p-col-12 whiteBg rounded p-text-left mt-5">
      <div class="p-col-12">
        <h2 class="title is-5 mb-5">{{ $t("webpage-logo") }}</h2>

        <div>
          <div class="mb-4" id="preview" v-if="logo.imageUrl">
            <img :src="logo.imageUrl" style="width: 150px" />
          </div>
          <input type="file" accept="image/*" @change="previewImage" id="file-input" />
          <div v-if="logo.image" class="mt-4">
            <Button @click="updateWebpage">{{ $t("Button.save") }}</Button>
          </div>
        </div>
      </div>
    </div>

    <div class="p-col-12 whiteBg rounded p-text-left mt-5">
      <div class="p-col-12">
        <h2 class="title is-5 mb-5">{{ $t("home-page-text") }}</h2>

        <div>
          <wysiwyg v-model="homeTextHtml" :placeholder="editorPlaceholder" />
          <div v-if="homeTextHtml" class="mt-4">
            <Button @click="updateWebpage">{{ $t("Button.save") }}</Button>
          </div>
        </div>
      </div>
    </div>

    <div class="p-col-12 whiteBg rounded p-text-left mt-5">
      <div class="p-col-12">
        <h2 class="title is-5 mb-5">{{ $t("choose-which-pages-are-displayed") }}</h2>

        <div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showStandingsPage" :binary="true" />
            <label>{{ $t("show-standings") }}</label>
          </div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showSchedulesPage" :binary="true" />
            <label>{{ $t("show-schedule") }}</label>
          </div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showScoresPage" :binary="true" />
            <label>{{ $t("show-scores") }}</label>
          </div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showTeamsPage" :binary="true" />
            <label>{{ $t("show-team-pages") }}</label>
          </div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showRostersPage" :binary="true" />
            <label>{{ $t("show-rosters") }}</label>
          </div>
          <div class="p-field-checkbox">
            <Checkbox v-model="showPlayersPage" @click="updatePagesModal" />
            <label>{{ $t("show-player-profiles") }}</label>
          </div>
          <Button class="mt-4" @click="updateWebpage">{{ $t("Button.save") }}</Button>
        </div>
      </div>
    </div>

    <Dialog
      :visible.sync="supportDialog"
      :modal="true"
      :closable="false"
      :style="{ maxWidth: '90%', width: '60%', minWidth: '450px' }"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ supportHeader }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeModal" />
        </h2>
      </template>

      <p>
        {{ supportMessage }}
        <a href="mailto:help@cactusware.com">help@cactusware.com</a>
      </p>
    </Dialog>

    <Dialog
      :visible.sync="editSlugDialog"
      :modal="true"
      :closable="false"
      :style="{ maxWidth: '90%', width: '60%', minWidth: '450px' }"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ dialogHeader }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeModal" />
        </h2>
      </template>
      
      <div class="p-d-flex p-justify-center p-align-left">
        <div class="p-mt-1">
          <div class="p-inputgroup">
            <span class="p-inputgroup-addon">{{ dialogUrl }}/</span>
            <InputText style="width: 30%" autofocus="true" v-model="newSlug" />
          </div>
          <small id="slug-help">{{ $t('use-alphanumeric-characters') }}</small>
        </div>
      </div>
      
      <div class="p-mt-1">
        <Button @click="dialogSave">{{ $t("Button.save") }}</Button>
      </div>

      <div class="p-mt-5">
        <p>
          {{ dialogMessage }}
          <a href="mailto:help@cactusware.com">help@cactusware.com</a>
        </p>
      </div>
    </Dialog>

    <Dialog
      :visible.sync="editSeasonDialog"
      :modal="true"
      :closable="false"
      :style="{ maxWidth: '90%', width: '60%', minWidth: '450px' }"
    >
      <template #header>
        <h2 class="title is-4 w100 has-text-centered">
          {{ $t("edit-public-webpage-season") }}
          <font-awesome-icon class="close-modal-icon" :icon="['fal', 'times-circle']" @click="closeModal" />
        </h2>
      </template>

      <div style="min-height: 220px">
        <div class="p-d-flex p-justify-center p-align-center">
          <Dropdown
            v-model="selectedSeasonId"
            :options="seasons"
            :value="selectedSeasonId"
            optionLabel="name"
            optionValue="id"
            :placeholder="$t('select-a-season')"
            class="w-full md:w-14rem"
          />

          <Button class="ml-4" @click="updateLeagueSeason">{{ $t("Button.save") }}</Button>
        </div>
      </div>
    </Dialog>
</div>
</template>

<script>
import api from "@/api";

export default {
  data() {
    return {
      webpage: {
        slug: "",
        url: "",
        team_pages: false,
        player_pages: false,
        leaderboard: false,
      },
      public_league_page: {
        slug: "",
        name: ""
      },
      leagueSeasons: {},
      logo: {
        image: null,
        imageUrl: null,
      },
      homeTextHtml: "",
      supportHeader: "",
      supportMessage: "",
      supportDialog: false,
      showStandingsPage: false,
      showSchedulesPage: false,
      showScoresPage: false,
      showTeamsPage: false,
      showRostersPage: false,
      showPlayersPage: false,
      showLeaguePage: false,
      showSaveButton: false,
      selectedSeason: {},
      selectedSeasonId: "",
      seasons: [],
      newSlug: "",
      isLoading: true,
      editSeasonDialog: false,
      createPublicLeagueDialog: false,
      editSlugDialog: false,
      dialogHeader: "",
      dialogMessage: "",
      dialogUrl: "",
      dialogSave: ""
    };
  },
  methods: {
    updateWebpage() {
      setTimeout(() => {
        this.isLoading = true;
      }, 300);
      const webpage = new FormData();
      webpage.append("public_webpage[id]", this.webpage.id);
      webpage.append("public_webpage[show_standings_page]", this.showStandingsPage);
      webpage.append("public_webpage[show_teams_page]", this.showTeamsPage);
      webpage.append("public_webpage[show_schedules_page]", this.showSchedulesPage);
      webpage.append("public_webpage[show_scores_page]", this.showScoresPage);
      webpage.append("public_webpage[show_rosters_page]", this.showRostersPage);
      if (this.logo.image) webpage.append("public_webpage[logo]", this.logo.image);
      if (this.homeTextHtml) webpage.append("public_webpage[home_text]", this.homeTextHtml);
      if (this.newSlug && this.newSlug.trim() !== "" && this.newSlug !== this.webpage.slug) {
        webpage.append("public_webpage[slug]", this.newSlug);
      } 
      api
        .updateWebpage(webpage)
        .then(response => {
          this.logo = {
            imageUrl: response.data.logo_url,
            image: null,
          };
          this.webpage.slug = response.data.slug;
          this.slugClaimed = response.data.slug_claimed;
          this.closeModal();
          this.makeToast("success", this.$t("Toast.public-webpage-has-been-updated"));
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        })
        .catch(err => {
          var error = err.response.data.errors;
          const errorMessages = [];
          for (const key in error) {
            if (error.hasOwnProperty(key)) {             
              error[key].forEach((message) => {
                errorMessages.push(`${key} ${message}`);
              });
            }
          }
          this.makeToast("error", errorMessages.join(','));
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        });
    },
    publishLeague() {
      api
        .updateLeaguePageDisplay(this.public_league_page.slug)
        .then(response => {
          this.showSaveButton = false;
          this.makeToast("success", this.$t('public-league-page-has-been-sucessfully-updated'));
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        })
        .catch(error => {
          this.makeToast("error", this.$t("Toast.error.something_wrong"));
        });
    },
    updateLeagueSeason() {
      if (!this.selectedSeasonId) {
        return this.makeToast("info", this.$t("Toast.please-select-a-season-first"));
      }

      setTimeout(() => {
        this.isLoading = true;
      }, 300);

      const leaguePage = new FormData();
      leaguePage.append("public_league_page[season_id]", this.selectedSeasonId);
      if (this.newSlug) leaguePage.append("public_league_page[slug]", this.newSlug);

      api
        .updateLeaguePageSeason(this.public_league_page.slug, leaguePage)
        .then(response => {
          this.selectedSeason = response.data.public_league_page.seasons[0];
          this.closeModal();
          if (this.newSlug)
          {
            this.fetchData();
          }
          this.makeToast("success", this.$t("Toast.public-webpage-has-been-updated"));
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        })
        .catch(error => {
          this.makeToast("error", error.response.data.errors);
          setTimeout(() => {
            this.isLoading = false;
          }, 300);
        });
    },
    updateUrl() {
      this.supportHeader = this.$t("edit-public-webpage-url");
      this.supportMessage = this.$t("please-contact-support-to-get-a-custom-url-for-your-league-at");
      this.supportDialog = true;
    },
    updateOrganizationSlug() {
      this.newSlug = "";
      this.dialogHeader = this.$t('edit-organization-url');
      this.dialogMessage = "You are allowed to change your organization's default url once. If you need to get a custom URL for your organizaton, please contact support at"
      this.dialogUrl = this.baseUrl;
      this.dialogSave = this.updateWebpage;
      this.editSlugDialog = true;
    },
    updateLeagueSlug(){
      this.newSlug = "";
      this.dialogHeader = this.$t('edit-league-url');
      this.dialogMessage = this.$t("please-contact-support-to-get-a-custom-url-for-your-league-at");
      this.dialogUrl = this.buildUrl;
      this.dialogSave = this.updateLeagueSeason;
      this.editSlugDialog = true;
    },
    updatePagesModal() {
      this.supportHeader = this.$t("choose-which-pages-are-displayed");
      this.supportMessage = this.$t("please-contact-support-to-change-these-displayed-public-pages-at");
      this.supportDialog = true;
    },
    previewImage(event) {
      const file = event.target.files[0];
      this.logo = {
        image: file,
        imageUrl: URL.createObjectURL(file),
      };
    },
    fetchData() {
      this.refreshLeagueInformation();
      api.getCurrentPublicLeaguePage(this.currentLeague.id).then(response => {
        this.webpage = response.data;
        this.public_league_page = response.data.public_league_page;
        this.selectedSeason = this.public_league_page.seasons[0];
        this.selectedSeasonId = this.public_league_page.seasons[0] && this.public_league_page.seasons[0].id;        
        this.seasons = this.currentLeague.seasons;
        if (response.data.home_text) this.homeTextHtml = response.data.home_text;
        this.logo = {
          imageUrl: response.data.logo_url,
          image: null,
        };
        this.showStandingsPage = response.data.show_standings_page;
        this.showTeamsPage = response.data.show_teams_page;
        this.showSchedulesPage = response.data.show_schedules_page;
        this.showScoresPage = response.data.show_scores_page;
        this.showRostersPage = response.data.show_rosters_page;
        this.showLeaguePage = response.data.public_league_page.published;
        this.slugClaimed = response.data.slug_claimed;
        this.isLoading = false;
      });
    },
    closeModal() {
      this.supportDialog = false;
      this.editSeasonDialog = false;
      this.editSlugDialog = false;
    },
  },
  computed: {
    baseUrl() {
      return `${process.env.VUE_APP_LANDING_PAGE_URL || "http://localhost:5000"}/leagues`;
    },
    buildUrl() {
      return `${process.env.VUE_APP_LANDING_PAGE_URL || "http://localhost:5000"}/leagues/${this.webpage.slug}`;
    },
    buildLeagueUrl() {
      return `${process.env.VUE_APP_LANDING_PAGE_URL || "http://localhost:5000"}/leagues/${this.webpage.slug}/${this.public_league_page.slug}`;
    },
    editorPlaceholder() {
      var organizationName = this.$store.getters.currentOrganization.name;
      return this.$t("editorPlaceholder", [organizationName]);
    },
    currentLeague() {
      return this.$store.getters.currentLeague;
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
